/**
 * Employee Cost Report Page
 * View calculated hourly cost per employee based on direct + allocated indirect costs
 */
import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api, toast } from "../App";
import {
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  Download,
  Filter,
} from "lucide-react";

export default function ExpenseEmployeeCost({ user }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  
  // Filter state
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterDepartment, setFilterDepartment] = useState("all");

  const departments = ["Projects", "Management", "Sales", "HR", "Finance", "Admin"];
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [filterMonth, filterYear, filterBranch, filterDepartment]);

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      let url = `/expenses/report/employee-cost?period_month=${filterMonth}&period_year=${filterYear}`;
      if (filterBranch && filterBranch !== "all") url += `&branch=${filterBranch}`;
      if (filterDepartment && filterDepartment !== "all") url += `&department=${filterDepartment}`;
      
      const response = await api.get(url);
      setReport(response.data);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const employees = report?.employees || [];
  const avgHourlyCost = employees.length > 0 
    ? employees.reduce((sum, e) => sum + e.hourly_cost, 0) / employees.length 
    : 0;
  const totalCost = employees.reduce((sum, e) => sum + e.total_cost, 0);
  const maxHourlyCost = employees.length > 0 ? Math.max(...employees.map(e => e.hourly_cost)) : 0;
  const minHourlyCost = employees.length > 0 ? Math.min(...employees.map(e => e.hourly_cost)) : 0;

  const getHourlyCostColor = (cost) => {
    if (cost < avgHourlyCost * 0.8) return "text-green-600";
    if (cost > avgHourlyCost * 1.2) return "text-red-600";
    return "text-blue-600";
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Hourly Cost Report</h1>
            <p className="text-gray-500">Calculated from direct costs + allocated indirect costs</p>
          </div>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Hourly Cost</p>
                  <p className="text-2xl font-bold text-blue-600">${avgHourlyCost.toFixed(2)}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Monthly Cost</p>
                  <p className="text-2xl font-bold">${totalCost.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Highest Rate</p>
                  <p className="text-2xl font-bold text-red-600">${maxHourlyCost.toFixed(2)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Employees</p>
                  <p className="text-2xl font-bold">{employees.length}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Month</Label>
                <Select value={filterMonth.toString()} onValueChange={(v) => setFilterMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select value={filterYear.toString()} onValueChange={(v) => setFilterYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Branch</Label>
                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Table */}
        <Card>
          <CardHeader>
            <CardTitle>Employee Cost Breakdown - {report?.period || ""}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Direct Costs</TableHead>
                  <TableHead className="text-right">Dept Share</TableHead>
                  <TableHead className="text-right">Indirect (Allocated)</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Hourly Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No employee cost data found. Add expenses to see calculations.
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow key={emp.employee_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{emp.employee_name}</div>
                          <div className="text-xs text-gray-500">{emp.employee_number}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{emp.department}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{emp.branch}</TableCell>
                      <TableCell className="text-right text-green-600">
                        ${emp.direct_costs.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell className="text-right text-blue-600">
                        ${emp.dept_direct_share.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell className="text-right text-purple-600">
                        ${emp.indirect_costs.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        {emp.allocation_percentage > 0 && (
                          <div className="text-xs text-gray-400">({emp.allocation_percentage}%)</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        ${emp.total_cost.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        {emp.working_hours}h
                      </TableCell>
                      <TableCell className={`text-right font-bold text-lg ${getHourlyCostColor(emp.hourly_cost)}`}>
                        ${emp.hourly_cost.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Legend */}
            {employees.length > 0 && (
              <div className="mt-4 flex gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Below avg (&lt;80%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Average range</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Above avg (&gt;120%)</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
